import { isa } from "@p0security/shared/types/is";
import { PermissionRequest } from "@p0security/shared/types/permission";
import {
  ActiveRequestStatuses,
  PendingRequestStatuses,
} from "@p0security/shared/types/request-status";
import { Drawer } from "antd";
import { useNavigateWithEnv } from "components/Assessment/hooks/useNavigateWithEnv";
import { Tenant } from "components/Login";
import { useAuthFetch } from "components/Login/hook";
import { SpaceBetweenDiv } from "components/divs";
import { useFirestoreDoc } from "providers/FirestoreProvider";
import { useCallback, useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { handleRequestActionResponse } from "../requestUtils";
import { ActiveRequestActions } from "./ActiveRequestActions";
import { ApproveRequestModal } from "./ApproveRequestModal";
import { PendingRequestActions } from "./PendingRequestActions";
import { RequestLogs } from "./RequestLogs";

export const RequestDetailDrawer: React.FC<{ requestId: string }> = ({
  requestId,
}) => {
  const tenantId = useContext(Tenant);
  const location = useLocation();
  const navigate = useNavigateWithEnv();

  const [requestProcessing, setRequestProcessing] = useState(false);
  const authFetch = useAuthFetch(undefined, setRequestProcessing);

  const { doc, loading } = useFirestoreDoc<PermissionRequest>(
    `o/${tenantId}/permission-requests/${requestId}`,
    { live: true }
  );
  const request = useMemo(() => doc?.data, [doc]);
  const isPending = isa(PendingRequestStatuses, request?.status ?? "");
  const isActive = isa(ActiveRequestStatuses, request?.status ?? "");

  const closeDrawer = useCallback(
    () => navigate({ ...location, pathname: ".." }, { relative: "path" }),
    [location, navigate]
  );

  const [approveModalOpen, setApproveModalOpen] = useState(false);

  const onApprove = useCallback(() => {
    setApproveModalOpen(true);
  }, []);
  const onDeny = useCallback(async () => {
    handleRequestActionResponse(
      await authFetch(`permission-requests/${requestId}/deny`, {
        method: "POST",
      }),
      "Request denied"
    );
  }, [requestId, authFetch]);

  const onRevoke = useCallback(async () => {
    handleRequestActionResponse(
      await authFetch(`permission-requests/${requestId}/revoke`, {
        method: "POST",
      }),
      "Access revoked"
    );
  }, [requestId, authFetch]);

  const closeModal = useCallback(() => {
    setApproveModalOpen(false);
  }, []);

  return (
    <Drawer
      title="Request Details"
      width="75%"
      open
      onClose={closeDrawer}
      styles={{ body: { paddingBottom: 80 } }}
      extra={
        <SpaceBetweenDiv>
          {isPending
            ? PendingRequestActions(onApprove, onDeny)
            : isActive
              ? ActiveRequestActions(doc, onRevoke, false)
              : null}
        </SpaceBetweenDiv>
      }
    >
      <RequestLogs requestId={requestId} tenantId={tenantId} />
      <ApproveRequestModal
        request={approveModalOpen ? doc : undefined}
        loading={loading || requestProcessing}
        onClose={closeModal}
      />
    </Drawer>
  );
};
